import { LogService } from '@/IsekaiRPG/shared/logging/logService';
import type { ZodType } from 'zod';
import {
  LLMJsonParseError,
  LLMMissingXmlTagError,
  LLMSchemaValidationError,
  LLMUpstreamError,
} from './llmErrors';

export class ResponseParser {
  private static TAG = 'ResponseParser';

  private static formatMessage(message: string, requestId?: string): string {
    return requestId ? `[${requestId}] ${message}` : message;
  }

  /**
   * 从 XML 标签中提取并解析 JSON
   * @param text LLM 返回的完整文本
   * @param tagName 目标标签名 (不带 <>)
   * @param schema Zod Schema 用于校验
   */
  static parseFromXml<T>(text: string, tagName: string, schema: ZodType<T>, context?: { requestId?: string }): T {
    const requestId = context?.requestId;
    const regex = new RegExp(`<${tagName}>([\\s\\S]*?)</${tagName}>`, 'i');
    const match = text.match(regex);

    if (!match) {
      const upstreamFailure = this.extractUpstreamFailureReason(text);
      if (upstreamFailure) {
        LogService.warn(
          this.TAG,
          this.formatMessage(`检测到上游错误响应（非 XML）：${upstreamFailure}`, requestId),
          text,
        );
        throw new LLMUpstreamError(`LLM 上游错误: ${upstreamFailure}`, {
          tagName,
          upstreamFailure,
          requestId,
        });
      }

      const preview = this.getPreview(text);
      LogService.warn(this.TAG, this.formatMessage(`未找到标签 <${tagName}>，回复片段：${preview}`, requestId), text);
      throw new LLMMissingXmlTagError(`Missing expected XML tag: <${tagName}> (preview: ${preview})`, {
        tagName,
        preview,
        requestId,
      });
    }

    let jsonString = match[1].trim();

    // 再次清理可能的 markdown 代码块
    jsonString = this.cleanMarkdown(jsonString);

    let data: unknown;
    try {
      data = this.parseJsonWithRepair(jsonString, tagName);
    } catch (e) {
      LogService.error(this.TAG, this.formatMessage('JSON 解析失败', requestId), e);
      throw new LLMJsonParseError(
        `JSON parse error: ${e instanceof Error ? e.message : String(e)}`,
        { tagName, requestId },
        e,
      );
    }

    const result = schema.safeParse(data);
    if (!result.success) {
      LogService.warn(this.TAG, this.formatMessage('Schema 校验失败', requestId), result.error);
      throw new LLMSchemaValidationError(
        `Data validation failed: ${JSON.stringify(result.error.flatten())}`,
        { tagName, requestId },
        result.error,
      );
    }

    return result.data as T;
  }

  private static cleanMarkdown(text: string): string {
    let cleaned = text.trim();
    if (cleaned.startsWith('```json')) cleaned = cleaned.substring(7);
    else if (cleaned.startsWith('```')) cleaned = cleaned.substring(3);
    if (cleaned.endsWith('```')) cleaned = cleaned.substring(0, cleaned.length - 3);
    return cleaned.trim();
  }

  /**
   * 解析 JSON（带一次轻量修复兜底）
   * 场景：LLM 在字符串值里输出了未转义的半角双引号，例如：
   * "description": "出现"裂颌蝙蝠群"与..."
   */
  private static parseJsonWithRepair(jsonString: string, tagName: string): unknown {
    const normalizedJson = this.normalizeUnicodeJsonPunctuation(jsonString);

    try {
      return JSON.parse(normalizedJson);
    } catch (firstError) {
      const repaired = this.repairLooseJson(normalizedJson);
      if (repaired !== normalizedJson) {
        try {
          const parsed = JSON.parse(repaired);
          LogService.warn(this.TAG, `标签 <${tagName}> 使用了宽松 JSON 修复解析`, firstError);
          return parsed;
        } catch {
          // 继续走原始错误，便于定位第一次失败原因。
        }
      }
      throw firstError;
    }
  }

  /**
   * 统一 JSON 结构中常见的全角 / 花式引号，减少中文输入法导致的结构破坏。
   * 这里只做低风险的结构性字符归一化，不主动改写普通中文文本。
   */
  private static normalizeUnicodeJsonPunctuation(input: string): string {
    return input
      .replace(/[“”„‟＂]/g, '"')
      .replace(/[‘’‚‛＇]/g, "'");
  }

  /**
   * 修复字符串内部未转义的 `"`：
   * - 若当前处于字符串内，遇到 `"` 且后续非合法闭合符（: , } ]）则视为内容引号并转义。
   */
  private static repairUnescapedQuotes(input: string): string {
    const chars = input.split('');
    let output = '';
    let inString = false;
    let escaped = false;

    const nextNonWhitespace = (start: number): string => {
      for (let i = start; i < chars.length; i += 1) {
        const c = chars[i];
        if (!/\s/.test(c)) return c;
      }
      return '';
    };

    for (let i = 0; i < chars.length; i += 1) {
      const ch = chars[i];

      if (!inString) {
        output += ch;
        if (ch === '"') {
          inString = true;
          escaped = false;
        }
        continue;
      }

      if (escaped) {
        output += ch;
        escaped = false;
        continue;
      }

      if (ch === '\\') {
        output += ch;
        escaped = true;
        continue;
      }

      if (ch === '"') {
        const next = nextNonWhitespace(i + 1);
        const isClosingQuote = next === ':' || next === ',' || next === '}' || next === ']';
        if (isClosingQuote) {
          output += ch;
          inString = false;
        } else {
          output += '\\"';
        }
        continue;
      }

      output += ch;
    }

    return output;
  }

  /**
   * 修复裸字符串值：
   * - 数组中: ["洞穴", 黑暗, 控制] -> ["洞穴", "黑暗", "控制"]
   * - 对象值中: {"name":Alice} -> {"name":"Alice"}
   *
   * 仅在以下场景触发：
   * - 当前位置位于 `:` / `[` / `,` 之后
   * - 下一个 token 不是 `"` / `{` / `[` / number / true / false / null
   */
  private static repairBareStringTokens(input: string): string {
    const chars = input.split('');
    let output = '';
    let inString = false;
    let escaped = false;
    let index = 0;

    const isWhitespace = (ch: string | undefined): boolean => Boolean(ch && /\s/.test(ch));
    const isDelimiter = (ch: string | undefined): boolean => ch === ',' || ch === ']' || ch === '}';

    const isNumberToken = (value: string): boolean => /^-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?$/.test(value);
    const isKeywordToken = (value: string): boolean => value === 'true' || value === 'false' || value === 'null';
    const shouldPreserveBareToken = (value: string): boolean => {
      if (!value) return true;
      if (isKeywordToken(value)) return true;
      if (isNumberToken(value)) return true;
      if (value.startsWith('{') || value.startsWith('[') || value.startsWith('"')) return true;
      return false;
    };

    while (index < chars.length) {
      const ch = chars[index];

      if (!inString) {
        output += ch;

        if (ch === '"') {
          inString = true;
          escaped = false;
          index += 1;
          continue;
        }

        const canStartBareValue = ch === ':' || ch === '[' || ch === ',';
        if (!canStartBareValue) {
          index += 1;
          continue;
        }

        let whitespaceEnd = index + 1;
        while (whitespaceEnd < chars.length && isWhitespace(chars[whitespaceEnd])) {
          output += chars[whitespaceEnd];
          whitespaceEnd += 1;
        }

        const nextChar = chars[whitespaceEnd];
        if (!nextChar || nextChar === '"' || nextChar === '{' || nextChar === '[' || nextChar === ']') {
          index = whitespaceEnd;
          continue;
        }

        let tokenEnd = whitespaceEnd;
        while (tokenEnd < chars.length && !isDelimiter(chars[tokenEnd])) {
          tokenEnd += 1;
        }

        const rawToken = chars.slice(whitespaceEnd, tokenEnd).join('');
        const normalizedToken = rawToken.trim();
        if (!normalizedToken || shouldPreserveBareToken(normalizedToken)) {
          output += rawToken;
          index = tokenEnd;
          continue;
        }

        const escapedToken = normalizedToken.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
        output += `"${escapedToken}"`;
        index = tokenEnd;
        continue;
      }

      if (escaped) {
        output += ch;
        escaped = false;
        index += 1;
        continue;
      }

      if (ch === '\\') {
        output += ch;
        escaped = true;
        index += 1;
        continue;
      }

      if (ch === '"') {
        output += ch;
        inString = false;
        index += 1;
        continue;
      }

      output += ch;
      index += 1;
    }

    return output;
  }

  private static repairLooseJson(input: string): string {
    const repairedQuotes = this.repairUnescapedQuotes(input);
    return this.repairBareStringTokens(repairedQuotes);
  }

  private static extractUpstreamFailureReason(text: string): string | null {
    const cleaned = this.cleanMarkdown(text);
    if (!cleaned) {
      return null;
    }

    const requestFailedMatch = cleaned.match(/\[?\s*请求失败[:：]\s*([^\]\n]+)\]?/i);
    if (requestFailedMatch?.[1]) {
      return requestFailedMatch[1].trim();
    }

    const patterns: Array<{ regex: RegExp; label: string }> = [
      { regex: /no capacity available/i, label: 'No capacity available' },
      { regex: /rate limit/i, label: 'Rate limit exceeded' },
      { regex: /insufficient[_\s-]?quota/i, label: 'Insufficient quota' },
      { regex: /model.+unavailable/i, label: 'Model unavailable' },
    ];

    for (const pattern of patterns) {
      if (pattern.regex.test(cleaned)) {
        return pattern.label;
      }
    }

    return null;
  }

  private static getPreview(text: string, maxLength: number = 160): string {
    const compact = this.cleanMarkdown(text).replace(/\s+/g, ' ').trim();
    if (compact.length <= maxLength) {
      return compact;
    }
    return `${compact.slice(0, maxLength)}...`;
  }
}
